import React, { useRef, useState } from "react";
import { Button, Modal, Table, Tag } from "antd";
import { Download, Upload, Loader2 } from "lucide-react";
import {
  exportProductsCsv,
  importProductsCsv,
  IBulkImportResult,
} from "../../../hooks/api/product/bulkCallbacks";
import { useMessageApi } from "../../../provider/AntdNotification";

// Export a spreadsheet, edit it, import it back - Oliver's testing doc,
// item 19: "avoids anyone needing to work directly against the database."
// Deliberately narrow: only title/short description/description/price are
// editable this way, matched by the product's own id. Preview-before-
// commit so a bad upload can be caught before it touches anything live.
export function ProductBulkActions() {
  const messageApi = useMessageApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<IBulkImportResult | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportProductsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `artparks-products-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingFile(file);
    setIsPreviewing(true);
    try {
      const result = await importProductsCsv(file, false);
      setPreview(result);
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to read CSV");
      setPendingFile(null);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingFile) return;
    setIsCommitting(true);
    try {
      const result = await importProductsCsv(pendingFile, true);
      messageApi.success(`Updated ${result.updated} product(s).`);
      setPreview(null);
      setPendingFile(null);
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setIsCommitting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={isExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        onClick={handleExport}
        disabled={isExporting}
      >
        Export CSV
      </Button>
      <Button
        size="small"
        icon={isPreviewing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        onClick={() => fileInputRef.current?.click()}
        disabled={isPreviewing}
      >
        Import CSV
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelected}
      />

      <Modal
        title="Review changes before importing"
        open={!!preview}
        onCancel={() => {
          setPreview(null);
          setPendingFile(null);
        }}
        onOk={handleConfirmImport}
        okText={`Apply ${preview?.preview.length || 0} change(s)`}
        okButtonProps={{ disabled: !preview?.preview.length, loading: isCommitting }}
        width={720}
      >
        {preview && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {preview.preview.length} row(s) have changes, {preview.skipped} unchanged.
              {preview.errors.length > 0 && (
                <span className="text-red-600"> {preview.errors.length} row(s) had errors and will be skipped.</span>
              )}
            </p>
            {preview.errors.length > 0 && (
              <div className="max-h-32 overflow-y-auto text-xs text-red-600 bg-red-50 rounded-lg p-2">
                {preview.errors.map((e, i) => (
                  <div key={i}>Row {e.row}: {e.message}</div>
                ))}
              </div>
            )}
            <Table
              size="small"
              rowKey="id"
              dataSource={preview.preview}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: "Title", dataIndex: "title" },
                {
                  title: "Fields changing",
                  dataIndex: "changes",
                  render: (changes: string[]) => (
                    <div className="flex flex-wrap gap-1">
                      {changes.map((c) => (
                        <Tag key={c}>{c}</Tag>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
